import {useEffect, useState} from 'react';
import {SessionExpirationConfiguration} from '../../config';
import {useOptionalCurrentUser} from '../../users/hooks/useOptionalCurrentUser';
import {AuthUser} from '../types';
import {useAuthUser} from './useAuthUser';
import {useLogout} from './useLogout';

interface CommonSessionState {
  loading: boolean;
}

interface CommonLoadedSessionState {
  canExtend: boolean;
  loading: false;
  willExpire: boolean;
}

export interface LoadingSessionState extends CommonSessionState {
  loading: true;
}

export interface ExpiringSessionState extends CommonLoadedSessionState {
  expiresAt: Date;
  willExpire: true;
}

export interface NonExpiringSessionState extends CommonLoadedSessionState {
  willExpire: false;
}

export type SessionState = LoadingSessionState | ExpiringSessionState | NonExpiringSessionState;

let sessionExpirationTimer: NodeJS.Timeout | undefined;
const clearSessionExpirationTimer = () => {
  if (sessionExpirationTimer) {
    globalThis.clearTimeout(sessionExpirationTimer);
    sessionExpirationTimer = undefined;
  }
};
const setSessionExpirationTimer = (onTimeout: () => Promise<void>, millisUntilExpiration: number) => {
  clearSessionExpirationTimer();
  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  sessionExpirationTimer = globalThis.setTimeout(onTimeout, millisUntilExpiration);
};

export function useSessionExpiration(
  sessionExpirationConfiguration: SessionExpirationConfiguration | undefined,
): SessionState {
  const maximumSessionLengthMinutes = sessionExpirationConfiguration?.maximumSessionLengthMinutes ?? 0;
  const sessionExtensionPeriodMinutes = sessionExpirationConfiguration?.sessionExtension?.extensionPeriodMinutes;
  const canExtend = (sessionExtensionPeriodMinutes ?? 0) > 0;
  const [sessionState, setSessionState] = useState<SessionState>(
    maximumSessionLengthMinutes > 0 ? {loading: true} : {canExtend, loading: false, willExpire: false},
  );
  const {data: authUser} = useAuthUser();
  const logout = useLogout({restoreLocationOnNextLogin: true});
  const currentUser = useOptionalCurrentUser()?.get();
  const lastSessionExtensionTimeMillis = currentUser?.sessionLastExtendedTime?.getTime() ?? 0;

  useEffect(() => {
    const scheduleSessionExpiration = async (authUser: AuthUser) => {
      const sessionExpirationTimeMillis = await computeSessionExpirationTimeMillis({
        ...sessionExpirationConfiguration,
        authUser,
        lastSessionExtensionTimeMillis,
        maximumSessionLengthMinutes,
      });
      const millisUntilExpiration = sessionExpirationTimeMillis - Date.now();
      setSessionState({canExtend, expiresAt: new Date(sessionExpirationTimeMillis), loading: false, willExpire: true});
      if (millisUntilExpiration < 0) {
        void logout();
      } else {
        setSessionExpirationTimer(logout, millisUntilExpiration);
      }
    };

    clearSessionExpirationTimer();

    if (authUser && maximumSessionLengthMinutes > 0) {
      void scheduleSessionExpiration(authUser);
    } else {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSessionState({canExtend: false, loading: false, willExpire: false});
    }

    return clearSessionExpirationTimer; // Clear the expiration timer when the hook is unmounted
  }, [
    authUser,
    canExtend,
    lastSessionExtensionTimeMillis,
    logout,
    maximumSessionLengthMinutes,
    sessionExpirationConfiguration,
    sessionExtensionPeriodMinutes,
    setSessionState,
  ]);

  return sessionState;
}

interface ComputeSessionExpirationTimeMillisOptions extends SessionExpirationConfiguration {
  authUser: AuthUser;
  lastSessionExtensionTimeMillis: number;
}
async function computeSessionExpirationTimeMillis({
  authUser,
  lastSessionExtensionTimeMillis,
  maximumSessionLengthMinutes,
  sessionExtension,
}: ComputeSessionExpirationTimeMillisOptions): Promise<number> {
  const authTimeMillis = Number((await authUser.getIdTokenResult()).claims.auth_time ?? 0) * 1_000;
  const currentSessionHasBeenExtended = lastSessionExtensionTimeMillis > authTimeMillis;
  const currentSessionStartTimeMillis = currentSessionHasBeenExtended ? lastSessionExtensionTimeMillis : authTimeMillis;
  const sessionDurationMinutes = currentSessionHasBeenExtended
    ? (sessionExtension?.extensionPeriodMinutes ?? 0)
    : maximumSessionLengthMinutes;
  const sessionDurationMillis = sessionDurationMinutes * 60 * 1_000;
  const sessionExpirationTimeMillis = currentSessionStartTimeMillis + sessionDurationMillis;
  return sessionExpirationTimeMillis;
}
